import { useMemo } from 'react';
import { View } from 'react-native';
import { Download, FileText } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { ventasPetshop } from '@/services/firebase/petshops';
import { usePetshopsMeta } from '@/hooks/usePetshopsMeta';
import { exportPetshopExcel, exportPetshopPDF } from '@/services/export';
import { colors } from '@/theme/tokens';
import type { PetshopId, PetshopVenta } from '@/types/domain';

import { FiltroPeriodo, enPeriodo, totalesDe, usePeriodo } from './periodo';
import { TablaVentas } from './TablaVentas';

interface Props {
  petshop: PetshopId;
}

/**
 * Evaluar ventas: el lapso que se elija y la misma tabla que en «Ventas y
 * rendiciones», con el TOTAL como primera fila. Las anuladas no cuentan.
 */
export function ResumenTab({ petshop }: Props) {
  const { metaDe } = usePetshopsMeta();
  const ventasQ = useMemo(() => ventasPetshop(petshop), [petshop]);
  const { data: ventas, loading } = useFirestoreCollection<PetshopVenta>(ventasQ);
  const periodo = usePeriodo('mes');

  const ventasFiltradas = useMemo(
    () => enPeriodo(ventas.filter((v) => !v.anulada), periodo.desde, periodo.hasta),
    [ventas, periodo.desde, periodo.hasta],
  );
  const totales = useMemo(() => totalesDe(ventasFiltradas), [ventasFiltradas]);

  const exportar = async (tipo: 'excel' | 'pdf') => {
    const meta = metaDe(petshop);
    const ctx = {
      petshop,
      petshopNombre: meta.nombre,
      petshopCorto: meta.corto,
      ventas: ventasFiltradas,
      rendiciones: [],
      periodoTxt: periodo.texto,
    };
    try {
      if (tipo === 'excel') await exportPetshopExcel(ctx);
      else await exportPetshopPDF(ctx);
    } catch (e: any) {
      alert(e?.message ?? 'No se pudo exportar.');
    }
  };

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <FiltroPeriodo
        periodo={periodo}
        acciones={
          ventasFiltradas.length > 0 ? (
            <>
              <Button label="Excel" variant="success" size="sm" iconLeft={<Download size={14} color={colors.white} />} onPress={() => exportar('excel')} />
              <Button label="PDF" variant="danger" size="sm" iconLeft={<FileText size={14} color={colors.white} />} onPress={() => exportar('pdf')} />
            </>
          ) : null
        }
      />

      {loading ? (
        <View style={{ padding: 16 }}>
          <SkeletonRows rows={4} />
        </View>
      ) : ventasFiltradas.length === 0 ? (
        <View style={{ padding: 12 }}>
          <EmptyState illustration="note" title="Sin ventas en este período" message="Probá con otro período o cargá ventas." />
        </View>
      ) : (
        <TablaVentas ventas={ventasFiltradas} totales={totales} />
      )}
    </Card>
  );
}
